import { DownloadOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Drawer, Input, Select, Table, Tabs, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdPlayRecordRow,
  AdPlaySummaryData,
  CommercialDimensionRow,
  CommercialRecordsData,
} from "../../shared/protocols/editor/PtlCommercialAnalyticsQuery";
import { DimensionTable, KpiStrip, TrendPanel } from "./CommercialAnalyticsWidgets";
import AdRevenueConfigView from "./AdRevenueConfigView";
import TencentAdRevenueReportsView from "./TencentAdRevenueReportsView";
import { ContentGrid, FilterBar, JsonBlock, Section, SectionHeader } from "./commercialAnalyticsStyles";
import {
  buildCommercialQuery,
  displayUser,
  downloadCsv,
  formatDateTime,
  formatDuration,
  formatInteger,
  formatPercent,
  optionRows,
  queryCommercialAnalytics,
} from "./commercialAnalyticsUtils";

interface AdPlayFilters {
  rangeDays: number;
  platform?: string;
  userId?: string;
  adType?: string;
  adUnitId?: string;
  adPlacementName?: string;
  success?: "true" | "false";
  isEnded?: "true" | "false";
  errCode?: string;
}

const DEFAULT_FILTERS: AdPlayFilters = { rangeDays: 7 };

export default function AdPlayAnalyticsView() {
  return (
    <Tabs
      defaultActiveKey="playback"
      destroyOnHidden={false}
      items={[
        { key: "playback", label: "播放数据", children: <AdPlayStatisticsView /> },
        { key: "revenue-config", label: "收益配置", children: <AdRevenueConfigView /> },
        { key: "tencent-reports", label: "腾讯上报明细", children: <TencentAdRevenueReportsView /> },
      ]}
    />
  );
}

function AdPlayStatisticsView() {
  const [draft, setDraft] = useState<AdPlayFilters>(DEFAULT_FILTERS);
  const [filters, setFilters] = useState<AdPlayFilters>(DEFAULT_FILTERS);
  const [summary, setSummary] = useState<AdPlaySummaryData>();
  const [records, setRecords] = useState<CommercialRecordsData>();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detail, setDetail] = useState<AdPlayRecordRow>();

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await queryCommercialAnalytics(buildCommercialQuery("adPlay", "summary", filters, {
        adType: filters.adType,
        adUnitId: filters.adUnitId,
        adPlacementName: filters.adPlacementName,
        success: readOptionalBoolean(filters.success),
        isEnded: readOptionalBoolean(filters.isEnded),
        errCode: filters.errCode,
      }));
      if (data.scope === "adPlay" && "kpis" in data) setSummary(data as AdPlaySummaryData);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters]);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const data = await queryCommercialAnalytics(buildCommercialQuery("adPlay", "records", filters, {
        adType: filters.adType,
        adUnitId: filters.adUnitId,
        adPlacementName: filters.adPlacementName,
        success: readOptionalBoolean(filters.success),
        isEnded: readOptionalBoolean(filters.isEnded),
        errCode: filters.errCode,
        page,
        pageSize,
      }));
      if ("rows" in data) setRecords(data as CommercialRecordsData);
    } finally {
      setRecordsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);
  useEffect(() => { void loadRecords(); }, [loadRecords]);

  const applyFilters = () => {
    setPage(1);
    setFilters({ ...draft });
  };

  const resetFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setPage(1);
    setFilters(DEFAULT_FILTERS);
  };

  const dimensionTabs = useMemo(() => [
    { key: "type", label: "广告类型", children: <DimensionTable rows={summary?.adTypes || []} loading={summaryLoading} showCompletion /> },
    { key: "placement", label: "广告位名称", children: <DimensionTable rows={summary?.adPlacements || []} loading={summaryLoading} showCompletion /> },
    { key: "unit", label: "广告位", children: <DimensionTable rows={summary?.adUnits || []} loading={summaryLoading} showCompletion /> },
    { key: "platform", label: "平台", children: <DimensionTable rows={summary?.platforms || []} loading={summaryLoading} showCompletion /> },
  ], [summary, summaryLoading]);

  const exportCurrentData = () => {
    const rows: Array<Record<string, unknown>> = [];
    if (summary) {
      Object.entries(summary.kpis).forEach(([key, value]) => rows.push({ 数据区: "概览", 指标: key, 数值: value }));
      appendDimensions(rows, "广告类型", summary.adTypes);
      appendDimensions(rows, "广告位名称", summary.adPlacements);
      appendDimensions(rows, "广告位", summary.adUnits);
      appendDimensions(rows, "平台", summary.platforms);
      summary.errors.forEach((row) => rows.push({
        数据区: "失败原因",
        广告类型: row.adType,
        错误码: row.errCode,
        错误信息: row.errMsg,
        上报数: row.count,
        玩家数: row.userCount,
      }));
    }
    (records?.rows as AdPlayRecordRow[] | undefined)?.forEach((row) => rows.push({
      数据区: "当前页明细",
      时间: formatDateTime(row.receivedAt),
      玩家: displayUser(row),
      用户ID: row.userId,
      平台: row.platform,
      广告类型: row.adType,
      广告位名称: row.adPlacementName,
      广告位: row.adUnitId,
      播放成功: row.success ? "是" : "否",
      完整播放: row.isEnded == null ? "未知" : row.isEnded ? "是" : "否",
      播放时长: formatDuration(row.durationMs),
      错误码: row.errCode,
      错误信息: row.errMsg,
    }));
    downloadCsv(`ad-play-analytics-${Date.now()}.csv`, rows);
  };

  const kpis = summary?.kpis;
  return (
    <>
      <FilterBar>
        <Select
          value={draft.rangeDays}
          style={{ width: 118 }}
          options={[{ label: "最近 1 天", value: 1 }, { label: "最近 7 天", value: 7 }, { label: "最近 30 天", value: 30 }, { label: "最近 90 天", value: 90 }]}
          onChange={(rangeDays) => setDraft((prev) => ({ ...prev, rangeDays }))}
        />
        <Input allowClear placeholder="平台" value={draft.platform} style={{ width: 120 }} onChange={(event) => setDraft((prev) => ({ ...prev, platform: event.target.value }))} />
        <Input allowClear placeholder="用户 ID" value={draft.userId} style={{ width: 190 }} onChange={(event) => setDraft((prev) => ({ ...prev, userId: event.target.value }))} />
        <Select
          allowClear
          showSearch
          placeholder="广告类型"
          value={draft.adType}
          style={{ width: 130 }}
          options={optionRows(summary?.options.adTypes)}
          onChange={(adType) => setDraft((prev) => ({ ...prev, adType }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="广告位名称"
          value={draft.adPlacementName}
          style={{ width: 155 }}
          options={optionRows(summary?.options.adPlacements)}
          onChange={(adPlacementName) => setDraft((prev) => ({ ...prev, adPlacementName }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="广告位"
          value={draft.adUnitId}
          style={{ width: 155 }}
          options={optionRows(summary?.options.adUnits)}
          onChange={(adUnitId) => setDraft((prev) => ({ ...prev, adUnitId }))}
        />
        <Select
          allowClear
          placeholder="播放结果"
          value={draft.success}
          style={{ width: 115 }}
          options={[{ label: "成功", value: "true" }, { label: "失败", value: "false" }]}
          onChange={(success) => setDraft((prev) => ({ ...prev, success }))}
        />
        <Select
          allowClear
          placeholder="完播状态"
          value={draft.isEnded}
          style={{ width: 115 }}
          options={[{ label: "完整播放", value: "true" }, { label: "未完整播放", value: "false" }]}
          onChange={(isEnded) => setDraft((prev) => ({ ...prev, isEnded }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="错误码"
          value={draft.errCode}
          style={{ width: 120 }}
          options={optionRows(summary?.options.errCodes)}
          onChange={(errCode) => setDraft((prev) => ({ ...prev, errCode }))}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>查询</Button>
        <Button onClick={resetFilters}>重置</Button>
        <Button
          icon={<ReloadOutlined />}
          loading={summaryLoading || recordsLoading}
          onClick={() => { void loadSummary(); void loadRecords(); }}
        >
          刷新
        </Button>
        <Button icon={<DownloadOutlined />} onClick={exportCurrentData}>导出 CSV</Button>
      </FilterBar>

      <KpiStrip items={[
        { label: "播放上报", value: formatInteger(kpis?.recordCount), hint: `${formatInteger(kpis?.userCount)} 名玩家` },
        { label: "播放成功", value: formatInteger(kpis?.succeededCount), hint: `成功率 ${formatPercent(kpis?.successRate)}` },
        { label: "播放失败", value: formatInteger(kpis?.failedCount), hint: "SDK 或业务播放失败" },
        { label: "完整播放", value: formatInteger(kpis?.endedCount), hint: `完播率 ${formatPercent(kpis?.completionRate)}` },
        { label: "完播已知", value: formatInteger(kpis?.endedKnownCount), hint: "仅作为完播率分母" },
        { label: "平均时长", value: formatDuration(kpis?.averageDurationMs), hint: "有时长记录的播放" },
      ]} />

      <ContentGrid>
        <TrendPanel
          title="广告播放趋势"
          rows={summary?.trends || []}
          loading={summaryLoading}
          secondaryLabel="完播"
          secondaryValue={(row) => row.endedCount || 0}
          barSeries={[
            { label: "激励广告", color: "#1677ff", value: (row) => row.rewardedVideoCount || 0 },
            { label: "插屏广告", color: "#fa8c16", value: (row) => row.interstitialCount || 0 },
          ]}
        />
        <Section>
          <SectionHeader>维度分布</SectionHeader>
          <Tabs size="small" items={dimensionTabs} />
        </Section>
      </ContentGrid>

      <Section>
        <SectionHeader>
          <span>广告失败原因</span>
          <Typography.Text type="secondary">仅展示前 20 项</Typography.Text>
        </SectionHeader>
        <Table
          rowKey="key"
          size="small"
          loading={summaryLoading}
          dataSource={summary?.errors || []}
          pagination={false}
          scroll={{ x: 600 }}
          columns={[
            { title: "广告类型", dataIndex: "adType", width: 130, render: emptyText },
            { title: "错误码", dataIndex: "errCode", width: 110, render: emptyText },
            { title: "错误信息", dataIndex: "errMsg", ellipsis: true, render: emptyText },
            { title: "次数", dataIndex: "count", width: 90, render: formatInteger },
            { title: "玩家", dataIndex: "userCount", width: 90, render: formatInteger },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader>
          <span>广告播放明细</span>
          <Typography.Text type="secondary">共 {formatInteger(records?.total)} 条</Typography.Text>
        </SectionHeader>
        <Table<AdPlayRecordRow>
          rowKey="_id"
          size="small"
          loading={recordsLoading}
          dataSource={(records?.rows || []) as AdPlayRecordRow[]}
          scroll={{ x: 1350 }}
          pagination={{
            current: page,
            pageSize,
            total: records?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize === pageSize ? nextPage : 1);
              setPageSize(nextPageSize);
            },
          }}
          columns={[
            { title: "上报时间", dataIndex: "receivedAt", width: 170, render: formatDateTime },
            { title: "玩家", width: 145, ellipsis: true, render: (_, row) => displayUser(row) },
            { title: "平台", dataIndex: "platform", width: 90, render: emptyText },
            { title: "广告类型", dataIndex: "adType", width: 120, render: emptyText },
            { title: "广告位名称", dataIndex: "adPlacementName", width: 150, ellipsis: true, render: emptyText },
            { title: "广告位", dataIndex: "adUnitId", width: 180, ellipsis: true, render: emptyText },
            { title: "结果", dataIndex: "success", width: 80, render: renderSuccess },
            { title: "完播", dataIndex: "isEnded", width: 80, render: renderEnded },
            { title: "时长", dataIndex: "durationMs", width: 90, render: formatDuration },
            { title: "错误", width: 210, ellipsis: true, render: (_, row) => row.errMsg || emptyText(row.errCode) },
            { title: "操作", fixed: "right", width: 76, render: (_, row) => <Button type="link" size="small" onClick={() => setDetail(row)}>详情</Button> },
          ]}
        />
      </Section>

      <Drawer title="广告播放详情" width="min(620px, 94vw)" open={!!detail} destroyOnHidden onClose={() => setDetail(undefined)}>
        <JsonBlock>{JSON.stringify(detail, null, 2)}</JsonBlock>
      </Drawer>
    </>
  );
}

function readOptionalBoolean(value: "true" | "false" | undefined): boolean | undefined {
  return value == null ? undefined : value === "true";
}

function appendDimensions(rows: Array<Record<string, unknown>>, section: string, dimensions: CommercialDimensionRow[]) {
  dimensions.forEach((row) => rows.push({
    数据区: section,
    分类: row.key,
    上报数: row.count,
    玩家数: row.userCount,
    成功率: formatPercent(row.successRate),
    完播率: formatPercent(row.completionRate),
    平均时长: formatDuration(row.averageDurationMs),
  }));
}

function renderSuccess(value: boolean) {
  return <Tag color={value ? "success" : "error"}>{value ? "成功" : "失败"}</Tag>;
}

function renderEnded(value: boolean | undefined) {
  if (typeof value !== "boolean") return <Tag>未知</Tag>;
  return <Tag color={value ? "success" : "warning"}>{value ? "是" : "否"}</Tag>;
}

function emptyText(value: unknown) {
  return value == null || value === "" ? "-" : String(value);
}
